// TDZ Guard (dev-only)
// @ts-nocheck — TODO: Add proper types (Phase 7)
if (import.meta.env.DEV) {
  await import('./utils/tdzGuard.ts');
}

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
// Client-side logging is handled by the unified logger

import App from './App';
// Trigger HMR update
import { AuthProvider } from '@/context';
import ErrorBoundary from './components/ErrorBoundary';
import LazyLoadErrorBoundary from './components/LazyLoadErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import { ThemeProvider } from './context/ThemeContextOptimized';
import { validateEnvironmentVariables } from './utils/envValidator';
import ProtectedRoute from './auth/ProtectedRoute';
import RoleRoute from './auth/RoleRoute';

// Import robust lazy loading utilities
import { createLazyComponent } from './utils/lazyImportWithRetry.js';
import {
  checkChunkErrorRedirect,
  setupGlobalChunkErrorHandler,
  initializeBuildTracking,
  checkStaleChunksOnNavigation
} from './utils/chunkErrorHandler.js';

// Lazy load route components with retry mechanism for race condition handling
const Login = createLazyComponent(() => import('./auth/login'), 'Login');
const Register = createLazyComponent(() => import('./auth/register'), 'Register');
const Account = createLazyComponent(() => import('./pages/Account'), 'Account');
const AdminPanel = createLazyComponent(() => import('./pages/AdminPanel'), 'AdminPanel');
const PatientsListPage = createLazyComponent(() => import('./components/patients-list/PatientsListPage'), 'PatientsListPage');
const AppointmentDashboard = createLazyComponent(() => import('./pages/AppointmentDashboard'), 'AppointmentDashboard');
const PatientChartPage = createLazyComponent(() => import('./pages/PatientChartPage'), 'PatientChartPage');
const PatientBillingPage = createLazyComponent(() => import('./pages/PatientBillingPage'), 'PatientBillingPage');
const TelehealthPage = createLazyComponent(() => import('./pages/TelehealthPage'), 'TelehealthPage');
const PatientJoinPage = createLazyComponent(() => import('./pages/PatientJoinPage'), 'PatientJoinPage');
const PrivacyCompliance = createLazyComponent(() => import('./pages/PrivacyCompliance'), 'PrivacyCompliance');
const ErrorPage = createLazyComponent(() => import('./pages/ErrorPage'), 'ErrorPage');
const LoggingExample = createLazyComponent(() => import('./components/LoggingExample'), 'LoggingExample');
const SecurityLogsPage = createLazyComponent(() => import('./components/security/SecurityLogsPage'), 'SecurityLogsPage');
const DataMaskingExample = createLazyComponent(() => import('./components/DataMaskingExample'), 'DataMaskingExample');
const PACSViewerPage = createLazyComponent(() => import('./pages/PACSViewerPage'), 'PACSViewerPage');
const PACSWorklistPage = createLazyComponent(() => import('./pages/PACSWorklistPage'), 'PACSWorklistPage');
const DICOMUploadPage = createLazyComponent(() => import('./pages/DICOMUploadPage'), 'DICOMUploadPage');
const QuarantinedUploadsPage = createLazyComponent(() => import('./pages/QuarantinedUploadsPage'), 'QuarantinedUploadsPage');
const LabManagerPage = createLazyComponent(() => import('./pages/LabManagerPage'), 'LabManagerPage');
const ClaimsQueuePage = createLazyComponent(() => import('./pages/ClaimsQueuePage'), 'ClaimsQueuePage');
const InboxPage = createLazyComponent(() => import('./pages/InboxPage'), 'InboxPage');
const FirebaseAuthHandler = createLazyComponent(() => import('./pages/FirebaseAuthHandler'), 'FirebaseAuthHandler');
const ChartImportPage = createLazyComponent(() => import('./pages/ChartImportPage'), 'ChartImportPage');


import './index.css';

// Client-side logging is initialized asynchronously

// Validate environment variables before rendering
const envValid = validateEnvironmentVariables();

// Note: ErrorBoundary provides catch-all error handling

// Declare window extension for dev tools
declare global {
  interface Window {
    createDemoAppointments?: unknown;
  }
}

// Initialize services in proper order
const initializeServices = async () => {
  // Step 1: Initialize Firebase first (critical for auth)
  try {
    const { setupNetworkMonitoring, setupFirestoreInternalErrorRecovery } = await import('./services/firebase.ts');
    setupFirestoreInternalErrorRecovery();
    await setupNetworkMonitoring();
    console.log('Firebase network monitoring initialized');
  } catch (error: unknown) {
    console.warn('Failed to setup Firebase network monitoring:', error);
  }

  // Step 2: Client logging is initialized after user authentication
  // This ensures logging code doesn't impact initial bundle size

  // Step 3: Load development tools
  if (import.meta.env.DEV) {
    try {
      const { createDemoAppointments } = await import('./utils/createDemoAppointments.js');
      window.createDemoAppointments = createDemoAppointments;
      console.log('Demo appointments creator loaded! Run: createDemoAppointments()');
    } catch (error: unknown) {
      console.warn('Failed to load demo appointments creator:', error);
    }
  }
};

// Initialize services if we're in the browser
if (typeof window !== 'undefined') {
  // Setup chunk error handling first
  setupGlobalChunkErrorHandler();
  checkChunkErrorRedirect();

  // Initialize build tracking
  initializeBuildTracking();

  // Production-only: Suppress known transient PACS/Cornerstone warnings
  // These are non-actionable (network retries, missing frames during streaming)
  // Keep dev noisy, silence prod
  if (import.meta.env.PROD) {
    const originalWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      const msg = (args[0] as { toString?: () => string })?.toString?.() ?? '';
      if (
        msg.includes('Image not found') ||
        msg.includes('Failed to fetch image') ||
        msg.includes("Couldn't process because") ||
        msg.includes('Viewport is too small')
      ) {
        return; // Swallow transient PACS/Cornerstone noise in production
      }
      originalWarn.apply(console, args);
    };

    // Suppress unhandled XMLHttpRequest rejections from PACS streaming
    // These happen during volume loading when frames fail to fetch
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      // XMLHttpRequest rejections during PACS streaming
      if (reason instanceof XMLHttpRequest || (reason as { constructor?: { name?: string } })?.constructor?.name === 'XMLHttpRequest') {
        event.preventDefault(); // Suppress the error
        return;
      }
    });
  }

  // Check for stale chunks on page load
  if (!checkStaleChunksOnNavigation()) {
    initializeServices();
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <React.Fragment>
      <ErrorBoundary
        fallbackMessage="The application encountered a critical error. Please refresh the page."
        showReset={false}
      >
        {/* Skip link for accessibility */}
        <a
          href="#main-content"
          className="skip-link"
          tabIndex={0}
        >
          Skip to main content
        </a>

        {!envValid ? (
          <div className="p-4 bg-red-100 text-red-800 rounded max-w-lg mx-auto mt-10">
            <h1 className="text-xl font-bold mb-2">Environment Configuration Error</h1>
            <p className="mb-4">The application is missing required configuration. Please check the console for details.</p>
            <p className="text-sm text-red-600">This error is only visible to administrators and developers.</p>
          </div>
        ) : (
          <ThemeProvider>
            <BrowserRouter
              {...{
                future: {
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }
              } as Record<string, unknown>}
            >
              <AuthProvider>
                <LazyLoadErrorBoundary
                  componentName="Application Routes"
                  onRetry={() => window.location.reload()}
                >
                  <Suspense fallback={<LoadingSpinner size="lg" text="Loading application..." />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/privacy" element={<PrivacyCompliance />} />
                      <Route path="/error" element={<ErrorPage />} />

                      {/* Patient Join Page (Public) */}
                      <Route path="/join/:inviteToken" element={<PatientJoinPage />} />

                      {/* Firebase Auth Handler (for OAuth callbacks on non-Firebase Hosting) */}
                      <Route path="/__/auth/handler" element={<FirebaseAuthHandler />} />

                      {/* Protected App Routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <App />
                          </ProtectedRoute>
                        }
                      >
                        {/* Schedule: clinic flow (Day) + week grid */}
                        <Route index element={<AppointmentDashboard />} />

                        {/* Patients list */}
                        <Route path="patients" element={<PatientsListPage />} />

                        {/* Patient Chart */}
                        <Route path="patients/:id" element={<PatientChartPage />} />

                        {/* Patient Billing */}
                        <Route path="patients/:id/billing" element={<PatientBillingPage />} />

                        {/* Telehealth Video Calls */}
                        <Route path="telehealth/:roomCode" element={<TelehealthPage />} />

                        {/* Account Page */}
                        <Route path="account" element={<Account />} />

                        {/* Admin Panel (Role-based) */}
                        <Route
                          path="admin"
                          element={
                            <RoleRoute allowedRoles={['admin']}>
                              <AdminPanel />
                            </RoleRoute>
                          }
                        />

                        {/* Security Logs (Admin only) */}
                        <Route
                          path="admin/security-logs"
                          element={
                            <RoleRoute allowedRoles={['admin']}>
                              <SecurityLogsPage />
                            </RoleRoute>
                          }
                        />

                        {/* Logging Example (Development only) */}
                        {import.meta.env.DEV && (
                          <Route path="logging-example" element={<LoggingExample />} />
                        )}

                        {/* Data Masking Example */}
                        <Route path="data-masking-example" element={<DataMaskingExample />} />

                        {/* PACS Viewer */}
                        <Route path="pacs" element={<PACSWorklistPage />} />
                        <Route path="pacs/upload" element={<DICOMUploadPage />} />
                        <Route path="pacs/upload/:patientId" element={<DICOMUploadPage />} />
                        <Route
                          path="pacs/quarantine"
                          element={
                            <RoleRoute allowedRoles={['admin']}>
                              <QuarantinedUploadsPage />
                            </RoleRoute>
                          }
                        />
                        <Route path="pacs/viewer/:studyId" element={<PACSViewerPage />} />

                        {/* Paper Chart Import */}
                        <Route path="chart-import" element={<ChartImportPage />} />
                        <Route path="chart-import/:patientId" element={<ChartImportPage />} />

                        {/* Lab Manager */}
                        <Route path="labs" element={<LabManagerPage />} />

                        {/* Clinical Inbox */}
                        <Route path="inbox" element={<InboxPage />} />

                        {/* Claims Queue */}
                        <Route path="claims" element={<ClaimsQueuePage />} />

                      </Route>
                    </Routes>
                  </Suspense>
                </LazyLoadErrorBoundary>
              </AuthProvider>
            </BrowserRouter>
          </ThemeProvider>
        )}
      </ErrorBoundary>
    </React.Fragment>
  </React.StrictMode>
);
